import React from 'react';
import { AlertTriangle, Zap } from 'lucide-react';
import { money } from '../../components/finance/financeFormat';
import {
  FINANCE_MODE_LABELS, scenarioTotalCents,
  type DealFinanceSummary, type DealScenario,
} from '../../services/dealPricing';

/**
 * WHAT A DEAL COSTS, IN ONE LIST CELL.
 *
 * A deal sells one offer several ways and the cell has room for one line, so it
 * states the cheapest monthly commitment and what is due today alongside it —
 * never a single figure standing for both. Upfront money and monthly money are
 * different kinds of money; adding them produces a number nobody is charged.
 *
 * Every figure comes from `dealScenarios`, which is what the deal EDITOR
 * prices from, so a row and the editor cannot describe one deal differently.
 */

const MODE_CHIP: Record<string, string> = {
  outright: 'bg-sky-100 text-sky-700',
  installment: 'bg-rose-100 text-rose-700',
  lease: 'bg-amber-100 text-amber-700',
  rental: 'bg-emerald-100 text-emerald-700',
  included: 'bg-stone-100 text-stone-600',
};

export const DealPriceCell: React.FC<{
  summary: DealFinanceSummary;
  /** Shown when the deal has no hardware at all — then the software price IS the price. */
  softwareMonthlyCents: number;
}> = ({ summary, softwareMonthlyCents }) => {
  const { minMonthly, minDown, best, bestTermMonths } = summary;

  // No hardware on the deal: there is one way to buy it, and a "from" price or
  // a best-value badge would both be theatre.
  if (!minMonthly || !best) {
    return (
      <div>
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-[15px] font-bold tabular-nums text-stone-800">{money(softwareMonthlyCents)}</span>
          <span className="text-[11px] text-stone-500">/mo</span>
        </div>
        <div className="mt-0.5 text-[10.5px] text-stone-400">software only · no hardware on this deal</div>
      </div>
    );
  }

  const down = minDown ?? best;
  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-[10.5px] text-stone-400">from</span>
        <span className="font-mono text-[15px] font-bold tabular-nums text-stone-800">{money(minMonthly.monthlyCents)}</span>
        <span className="text-[11px] text-stone-500">/mo</span>
        <span className="text-[11px] text-stone-300">·</span>
        <span className={`font-mono text-[12px] font-semibold tabular-nums ${
          down.visibleDueTodayCents === 0 ? 'text-emerald-700' : 'text-stone-600'
        }`}
        >
          {down.visibleDueTodayCents === 0 ? '$0 down' : `${money(down.visibleDueTodayCents)} down`}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-[1.5px] text-[10px] font-bold text-emerald-800">
          <Zap className="h-2.5 w-2.5" /> Best over {bestTermMonths}mo
        </span>
        <span className="text-[10.5px] text-stone-400">{FINANCE_MODE_LABELS[best.mode] || best.mode}</span>
      </div>
    </div>
  );
};

/**
 * THE FULL BREAKDOWN — one tile per way the offer can be sold, the same three
 * (or four) the deal editor's Bundles section shows.
 *
 * The badges are the point: MIN /MO, MIN DOWN and BEST are frequently different
 * modes, and seeing which is which is what stops an operator quoting the
 * cheapest monthly to a customer who cannot find the deposit for it.
 */
export const DealScenarioTiles: React.FC<{ summary: DealFinanceSummary }> = ({ summary }) => {
  const { scenarios, minMonthly, minDown, best, bestTermMonths } = summary;
  if (scenarios.length === 0) {
    return <p className="text-[11.5px] text-stone-400">This deal includes no hardware, so there is nothing to finance.</p>;
  }

  const fallbacks = (s: DealScenario) => s.rows.filter((r) => r.fallback);

  return (
    <div>
      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {scenarios.map((s) => {
          const isBest = best?.mode === s.mode;
          return (
            <div
              key={s.mode}
              className={`rounded-xl border-[1.5px] p-[9px_12px] text-left transition ${
                isBest ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200' : 'border-stone-200 bg-white'
              }`}
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={`rounded-full px-1.5 py-[1.5px] text-[10px] font-bold uppercase ${MODE_CHIP[s.mode] || 'bg-stone-100 text-stone-600'}`}>
                  {FINANCE_MODE_LABELS[s.mode] || s.mode}
                </span>
                {minMonthly?.mode === s.mode && (
                  <span className="rounded-full bg-stone-800 px-1.5 py-[1.5px] text-[9px] font-bold text-white">MIN /MO</span>
                )}
                {minDown?.mode === s.mode && (
                  <span className="rounded-full bg-stone-800 px-1.5 py-[1.5px] text-[9px] font-bold text-white">MIN DOWN</span>
                )}
                {isBest && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-1.5 py-[1.5px] text-[9px] font-bold text-white">
                    <Zap className="h-2 w-2" /> BEST
                  </span>
                )}
              </div>

              <div className="mt-1.5 font-mono text-[13px] font-bold tabular-nums text-stone-800">
                {money(s.monthlyCents)}<span className="text-[10.5px] font-medium text-stone-500">/mo</span>
              </div>
              <div className={`font-mono text-[11px] tabular-nums ${
                s.visibleDueTodayCents === 0 ? 'font-semibold text-emerald-700' : 'text-stone-500'
              }`}
              >
                {s.visibleDueTodayCents === 0 ? '$0 upfront' : `${money(s.visibleDueTodayCents)} upfront`}
              </div>
              <div className="mt-1 font-mono text-[10px] tabular-nums text-stone-400">
                {bestTermMonths}mo total {money(scenarioTotalCents(s, bestTermMonths))}
              </div>

              {s.hasHiddenDueToday && (
                <div className="mt-1 text-[10px] text-stone-400">plus charges withheld from the customer preview</div>
              )}
              {fallbacks(s).length > 0 && (
                <div className="mt-1 flex items-start gap-1 text-[10px] leading-snug text-amber-700">
                  <AlertTriangle className="mt-[1px] h-2.5 w-2.5 shrink-0" />
                  <span>
                    {fallbacks(s).map((r) => `${r.deviceName} is ${r.saleMode}-only`).join('; ')}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-[10.5px] leading-snug text-stone-400">
        <b>MIN /MO</b> is the lowest monthly, <b>MIN DOWN</b> the least due today, <b>BEST</b> the lowest
        {' '}upfront + monthly × {bestTermMonths} months — the term this deal itself finances.
        Upfront and monthly are never added into one headline.
      </p>
    </div>
  );
};
